import React, { useEffect, useState } from 'react';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import { IAmendmentPageState, IAmendmentTravelDetails } from '..';
import { IAmendmentGeneralData } from '../line-amendment';
import { useLazyQuery } from '@apollo/client';
import { getTravelLovsQuery } from '../queries';
import { getTravelLovs } from '../utils';
import Loader from '@components/loader/Loader';
import SelectFormField from '@components/form-fields/SelectFormField';
import MultiselectFormField from '@components/form-fields/MultiselectFormField';
import { useGridStyles } from '@styles/sharedStyles';

interface ITravelDetailsWidgetProps {
  generalData: IAmendmentGeneralData;
  pageState: IAmendmentPageState;
  onPageStateUpdate: (pageState: IAmendmentPageState) => void;
  isDisabled?: boolean;
}

export interface ITravelLovs {
  destinations: Record<string, string>;
  policyCovers: Record<string, string>;
}

const TravelDetailsWidget: React.FC<ITravelDetailsWidgetProps> = ({
  generalData,
  pageState,
  onPageStateUpdate,
  isDisabled,
}) => {
  const { classes } = useGridStyles({
    fieldsPerRow: 2,
  });

  const values = pageState.values.travelDetails;
  const errors = pageState.errors.travelDetails;
  const touched = pageState.touched.travelDetails;

  const [lovs, setLovs] = useState<ITravelLovs>({
    destinations: {},
    policyCovers: {},
  });
  const [getTravelLovsLazy, { loading }] = useLazyQuery(getTravelLovsQuery);

  const initialize = async () => {
    const { data: lovsData } = await getTravelLovsLazy({
      variables: {
        selectedPlanID: generalData.plan.id,
      },
    });

    const lists = getTravelLovs(lovsData);
    setLovs({
      destinations: lists.destinations,
      policyCovers: lists.policyCovers,
    });
  };

  useEffect(() => {
    initialize();
  }, []);

  const onFieldBlur = (fieldName: keyof IAmendmentTravelDetails) => {
    const newPageState = { ...pageState };
    newPageState.touched.travelDetails[fieldName] = true;
    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IAmendmentTravelDetails,
    value: unknown
  ) => {
    const newPageState = { ...pageState };
    (newPageState.values.travelDetails as any)[fieldName] = value;
    onPageStateUpdate(newPageState);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <WidgetPaper>
      <WidgetSection title="Travel Details" isFieldsGrid fieldsPerRow={2}>
        <SelectFormField
          key="destination"
          name="destination"
          title="Destination*"
          placeholder="Destination"
          value={values.destination}
          error={touched.destination ? errors.destination : ''}
          onChange={(v) => onFieldUpdate('destination', v)}
          disabled={isDisabled}
          selectOptions={lovs.destinations}
          material
        />

        <NumberInputFormField
          key="duration"
          name="duration"
          title="Duration (in Days)*"
          placeholder="Duration"
          value={values.duration}
          error={touched.duration ? errors.duration : ''}
          onBlur={() => onFieldBlur('duration')}
          onChange={(e) => onFieldUpdate('duration', e.target.value)}
          disabled={isDisabled}
          material
        />

        <MultiselectFormField
          key="policyCover"
          name="policyCover"
          title="Policy Cover*"
          placeholder="Policy Cover"
          values={values.policyCover}
          error={touched.policyCover ? errors.policyCover : ''}
          onChange={(v) => onFieldUpdate('policyCover', v)}
          disabled={isDisabled}
          selectOptions={lovs.policyCovers}
          material
          className={classes.fullWidthField}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export default TravelDetailsWidget;
